import { useEffect, useState } from 'react'
import { href, Link } from 'react-router'
import Button from '~/components/webshop-button'
import type { loader as layoutLoader } from '~/routes/_layout'
import {
  acceptAllCookies,
  getConsentPreferences,
  rejectAllCookies,
  saveConsentPreferences,
  type CookieConsentPreferences
} from '~/utils/cookie-consent'
import type { MetaFunction } from 'react-router'

export const meta: MetaFunction<Record<string, never>, { layout: typeof layoutLoader }> = ({ matches }) => {
  const layoutData = matches.find(m => m.id === 'layout')?.data
  const companyName = layoutData?.orgDetails?.companyName ?? 'Webshop'
  const canonicalUrl = layoutData?.canonicalUrl ? `${layoutData.canonicalUrl}/cookie-instellingen` : undefined

  const title = `Cookie-instellingen | ${companyName}`
  const description = 'Beheer uw cookievoorkeuren en privacy-instellingen. Kies welke cookies u accepteert: essentieel, functioneel of analytisch.'

  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:locale', content: 'nl_NL' },
    { name: 'robots', content: 'noindex, nofollow' },
    { tagName: 'link', rel: 'canonical', href: canonicalUrl }
  ]
}

type ConsentCategory = {
  id: keyof Pick<CookieConsentPreferences, 'essential' | 'functional' | 'analytics'>
  label: string
  description: string
  details: string[]
  required: boolean
}

const CONSENT_CATEGORIES: ConsentCategory[] = [
  {
    id: 'essential',
    label: 'Essentieel',
    description: 'Deze cookies zijn noodzakelijk voor de basisfunctionaliteit van de website.',
    details: ['Winkelwagen functionaliteit (30 dagen)', 'Authenticatie en inloggen', 'Beveiligingsfuncties', 'Cookie-voorkeuren opslaan (1 jaar)'],
    required: true
  },
  {
    id: 'functional',
    label: 'Functioneel',
    description: 'Deze cookies verbeteren uw gebruikerservaring door uw voorkeuren te onthouden.',
    details: ['Onthouden van ingevulde formuliergegevens (1 jaar)', 'Taalvoorkeuren', 'Eerder bekeken producten'],
    required: false
  },
  {
    id: 'analytics',
    label: 'Analytisch',
    description: 'Deze cookies helpen ons de website te verbeteren door inzicht te geven in het gebruik.',
    details: ['PostHog analytics (anonieme gebruiksstatistieken)', 'Paginabezoeken en navigatiepatronen', 'Technische informatie over uw apparaat'],
    required: false
  }
]

export default function CookieSettings() {
  const [preferences, setPreferences] = useState<CookieConsentPreferences>({
    essential: true,
    functional: false,
    analytics: false,
    consented: false,
    timestamp: 0
  })
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setPreferences(getConsentPreferences())
  }, [])

  const handleToggle = (categoryId: 'functional' | 'analytics') => {
    setPreferences(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }))
    setSaved(false)
  }

  const handleSave = () => {
    saveConsentPreferences({
      functional: preferences.functional,
      analytics: preferences.analytics
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const handleAcceptAll = () => {
    acceptAllCookies()
    setPreferences(getConsentPreferences())
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const handleRejectAll = () => {
    rejectAllCookies()
    setPreferences(getConsentPreferences())
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  return (
    <div className="mx-auto w-full max-w-3xl flex-1 p-4 lg:p-2 lg:pt-4">
      <div className="shadow-card rounded-(--ws-radius) bg-white p-6 sm:p-8">
        <h1 className="mb-2 text-2xl font-bold text-gray-900">Cookie-instellingen</h1>
        <p className="mb-6 text-sm text-gray-600">
          Hieronder kunt u uw cookievoorkeuren aanpassen. Lees ons{' '}
          <Link to={href('/privacy-policy')} className="text-(--ws-link) underline hover:no-underline">
            privacybeleid
          </Link>{' '}
          voor meer informatie over hoe wij uw gegevens verwerken.
        </p>

        <div className="space-y-4">
          {CONSENT_CATEGORIES.map(category => (
            <div key={category.id} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold text-gray-900">{category.label}</h2>
                    {category.required && <span className="rounded bg-gray-200 px-1.5 py-0.5 text-xs text-gray-600">Altijd aan</span>}
                  </div>
                  <p className="mt-1 text-sm text-gray-600">{category.description}</p>
                  <ul className="mt-2 space-y-1">
                    {category.details.map((detail, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-gray-500">
                        <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-gray-400" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={category.required || preferences[category.id]}
                    disabled={category.required}
                    onChange={() => !category.required && handleToggle(category.id as 'functional' | 'analytics')}
                    className="peer sr-only"
                  />
                  <div className="peer h-6 w-11 rounded-full bg-gray-300 peer-checked:bg-(--ws-btn2-bg) peer-disabled:cursor-not-allowed peer-disabled:opacity-50 after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white" />
                </label>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-2">
            <Button fullWidth={false} onClick={handleRejectAll}>
              Alles weigeren
            </Button>
            <Button fullWidth={false} onClick={handleAcceptAll}>
              Alles accepteren
            </Button>
          </div>
          <Button
            fullWidth={false}
            onClick={handleSave}
            bgColor="var(--ws-btn2-bg)"
            bgColorHover="var(--ws-btn2-bg-hover)"
            textColor="var(--ws-btn2-text)">
            {saved ? 'Opgeslagen!' : 'Voorkeuren opslaan'}
          </Button>
        </div>

        {preferences.consented && (
          <p className="mt-4 text-xs text-gray-400">
            Laatst bijgewerkt:{' '}
            {new Date(preferences.timestamp).toLocaleDateString('nl-NL', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        )}
      </div>
    </div>
  )
}
